import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
    ensure,
    FormMultiSelectList,
    FormValidationSummary,
    getById,
    mapToDropdownOption,
    Stack,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import { CreateContentVersionButton } from "../../../../common/components/content-version/CreateContentVersionButton";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { isTrailerVersionType } from "../../../../common/types/TrailerContentVersion";
import { renderBrandedContentVersionText } from "../../../../common/utils/renderContentVersionText";
import { useAssociateContentFormContext } from "../context/useAssociateContentFormContext";
import { ensureMovieAssociateDetails } from "../types/ContentAssociationDetails";
import { ContentAssociationFormState } from "../types/ContentAssociationFormState";
import { renderTrailerVersionInfo } from "../utils/renderTrailerVersionInfo";
import { createContentAssociationMovieItemValidator } from "./ContentAssociationMovieItemValidator";
import { useContentAssociationMovieForm } from "./useContentAssociationMovieForm";

export const ContentAssociationMovieForm = (): JSX.Element => {
    const { control, errors } = useFormContext<ContentAssociationFormState>();
    const [movieValidationResult, setMovieValidationResult] = useState<ValidationResult>();

    const { contentDetails, validation, setValidationResult, isEditDisabled } = useAssociateContentFormContext();

    ensure(contentDetails);
    ensureMovieAssociateDetails(contentDetails);

    const validator = createContentAssociationMovieItemValidator();

    const { init, opts, addNewVersion } = useContentAssociationMovieForm();

    useEffect(() => {
        init();

        const result = validator.validateMovie(contentDetails);
        setMovieValidationResult(result);
    }, [contentDetails]);

    const shouldDisableVersionOpt = (itemId: string, selectedIds: string[]): boolean => {
        if (isEditDisabled) return true;

        const versions = opts.versions;

        const movieVersion = versions.filter((version) => selectedIds.includes(version.id));
        const versionToCheck = getById(versions, itemId);

        if (movieVersion.length === 0) return false;

        return !validator.isMatchingVersion(versionToCheck, movieVersion);
    };

    const validateVersion = (itemId: string): ValidationResult => {
        const versions = opts.versions;

        const versionToValidate = getById(versions, itemId);

        return validator.validateVersion(versionToValidate, versions);
    };

    const validateSelectedVersions = (versionIds: string[]): ValidationResult => {
        return validation.actions?.validateVersions?.(opts.versions, versionIds) ?? validationSuccessful();
    };

    const shouldRenderTrailerInfo = (itemId: string) => {
        if (!opts.versions.length) return false;

        const versionToCheck = opts.versions.find((v) => v.id === itemId);

        return isTrailerVersionType(versionToCheck?.type);
    };

    const renderAdditionalInfo = (itemId: string, isItemInactive?: boolean) => {
        if (!shouldRenderTrailerInfo(itemId)) {
            return <></>;
        }

        return renderTrailerVersionInfo({ isItemInactive });
    };

    const createContentVersionButtonView = (
        <Stack style={{ marginLeft: "5px" }}>
            <CreateContentVersionButton
                disabled={isEditDisabled}
                content={{
                    type: ContentReferenceType.Movie,
                    movieId: contentDetails.id
                }}
                onContentVersionCreated={addNewVersion}
            />
        </Stack>
    );

    return (
        <>
            <FormMultiSelectList
                items={opts.versions.map((opt) => mapToDropdownOption(opt, () => renderBrandedContentVersionText(opt)))}
                onChange={() => setValidationResult(undefined)}
                control={control}
                errors={errors}
                rules={{
                    required: validation.rules.areVersionsRequired ? "Select version" : false,
                    validate: validateSelectedVersions
                }}
                name="versionIds"
                actionButtons={[createContentVersionButtonView]}
                label="Versions"
                disabled={!opts.versions.length || isEditDisabled}
                isItemDisabled={shouldDisableVersionOpt}
                renderAdditionalInfo={renderAdditionalInfo}
                onItemValidating={validateVersion}
                minHeight={150}
                maxHeight={150}
            />
            <FormValidationSummary validationResult={movieValidationResult} />
        </>
    );
};
